<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-agedbalance">
        <i class="far fa-envelope-open-dollar"></i> Aged Receivables
      </Tab>
      <Tab tabid="tab-statements">
        <i class="far fa-paper-plane"></i> Send Statements
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-agedbalance">
        <div class="row no-gutter">
          <div class="col-lg-6">
            <ContentTitle>Aged Receivables - <span v-if="!appliedReceivableFilter.contactId">All Contacts</span><span v-if="appliedReceivableFilter.contactId">{{appliedReceivableFilter.contactName}}</span></ContentTitle>
            <ContentSubTitle>Showing contacts accounts as at {{appliedReceivableFilter.date | formatDate}}</ContentSubTitle>
          </div>
          <div class="col-lg-6">
            <ReportFilterPopover
              class="float-right"
              headerTitle="Ages Receivables Filter"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applyReceivableFilter"
              @reset="resetReceivableFilter">
              <template v-slot:popover>
                <ReportFilterGroup label="Statements As At Date">
                  <ReportDateSelect 
                    :date="editingReceivableFilter.date"
                    @updateDate="editingReceivableFilter.date = $event"
                    :minDate="minStatementDate"
                  />
                </ReportFilterGroup>
                <ReportFilterGroup label="Contact">
                  <ReportContactSelect
                    v-model="editingReceivableFilter.contactId"
                    @updateName="editingReceivableFilter.contactName = $event"
                  />
                </ReportFilterGroup>
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedReceivableFilter.date | formatDate}}
                </div>
              </template>
            </ReportFilterPopover>
          </div>
        </div>

        <LoadingPanel :loading="loading" :loadingError="loadingError" />

        <div class="row mt-2" v-if="agedTrialBalanceData.contactList">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list" striped hover>
              <template v-slot:header>
                <tr>
                  <th>Contact</th>

                  <th class="text-center">
                    <Tooltip helpStyle label="Total invoiced amount owing minus available credit notes / overpayments">Total</Tooltip>  
                  </th>
                  <th class="text-center">
                    <Tooltip helpStyle label="Total invoiced not due amount owing minus available credit notes / overpayments">Current</Tooltip>
                  </th>
                  <th class="text-center">Due 0 - 30 Days</th>
                  <th class="text-center">Due 30 - 60 Days</th>
                  <th class="text-center">Due 60 - 90 Days</th>
                  <th class="text-center">Due 90+ Days</th>
                  <th class="text-center">Last Payment</th>
                  <th class="text-center">Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <template v-for="contact in agedTrialBalanceData.contactList">
                  <tr :key="`contact-${contact.id}`">
                    <td class="font-weight-bold">
                      <LinkContact :id="contact.id" :name="contact.name" />
                    </td>

                    <td class="text-center">{{contact.outstanding | formatCurrencyAccounts}}</td>
                    <td class="text-center">{{contact.outstandingCurrent | formatCurrencyAccounts}}</td>
                    <td class="text-center">{{contact.invoiceDue0to30 | formatCurrencyAccounts}}</td>
                    <td class="text-center">{{contact.invoiceDue30to60 | formatCurrencyAccounts}}</td>
                    <td class="text-center">{{contact.invoiceDue60to90 | formatCurrencyAccounts}}</td>
                    <td class="text-center">{{contact.invoiceDue90Plus | formatCurrencyAccounts}}</td>
                    <td class="text-center">
                      <div v-if="contact.lastPaymentDate && contact.lastPaymentAmount">
                        {{contact.lastPaymentDate | formatDate}}<br />
                        {{contact.lastPaymentAmount | formatCurrencyAccounts}}<br />
                      </div>
                    </td>
                    <td class="text-center">
                      <ActionDropMenu :tableAction="true" :forceDropdown="true">
                        <DropdownMenuItem @click="displayBalanceStatement(contact.id)" icon="fa-file-invoice-dollar">Balance Statement</DropdownMenuItem>
                      </ActionDropMenu>                      
                    </td>
                  </tr>
                </template>

                <tr class="table-totals-row-header font-weight-bold">
                  <td>Totals</td>
                  <td class="text-center">{{agedTrialBalanceData.totalOutstanding | formatCurrencyAccounts}}</td>
                  <td class="text-center">{{agedTrialBalanceData.totalOutstandingCurrent | formatCurrencyAccounts}}</td>
                  <td class="text-center">{{agedTrialBalanceData.totalInvoiceDue0to30 | formatCurrencyAccounts}}</td>
                  <td class="text-center">{{agedTrialBalanceData.totalInvoiceDue30to60 | formatCurrencyAccounts}}</td>
                  <td class="text-center">{{agedTrialBalanceData.totalInvoiceDue60to90 | formatCurrencyAccounts}}</td>
                  <td class="text-center">{{agedTrialBalanceData.totalInvoiceDue90Plus | formatCurrencyAccounts}}</td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                </tr>
              </template>
              <template v-slot:empty v-if="!agedTrialBalanceData.contactList || agedTrialBalanceData.contactList.length == 0">
                No contact data found for the date range
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-statements">
        <div class="row no-gutter">
          <div class="col-lg-6">
            
            <ContentTitle>Statements - <span v-if="!appliedReceivableFilter.contactId">All Contacts</span><span v-if="appliedReceivableFilter.contactId">{{appliedReceivableFilter.contactName}}</span></ContentTitle>
            <ContentSubTitle>Showing contacts statements as at {{appliedReceivableFilter.date | formatDate}}</ContentSubTitle>
          </div>
          <div class="col-lg-6">
            <ActionDropMenu class="float-right ml-2">
              <DropdownMenuItem @click="sendSelectedStatements" icon="fa-paper-plane">Send Selected Statements</DropdownMenuItem>
            </ActionDropMenu>

            <ReportFilterPopover
              class="float-right"
              headerTitle="Ages Receivables Filter"
              :showApplyButton="true"
              :showResetButton="true"
              @apply="applyReceivableFilter"
              @reset="resetReceivableFilter">
              <template v-slot:popover>
                <ReportFilterGroup label="Statements As At Date">
                  <ReportDateSelect v-model="editingReceivableFilter.date" :minDate="minStatementDate" />
                </ReportFilterGroup>
                <ReportFilterGroup label="Contact">
                  <ReportContactSelect
                    v-model="editingReceivableFilter.contactId"
                    @updateName="editingReceivableFilter.contactName = $event"
                  />
                </ReportFilterGroup>
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedReceivableFilter.date | formatDate}}
                </div>
              </template>
            </ReportFilterPopover>
          </div>
        </div>

        <LoadingPanel :loading="loading" :loadingError="loadingError" />

        <div class="row mt-2" v-if="statementContactList">
          <div class="col-lg-12">
            <StaticDataTable class="static-table-list" striped hover>
              <template v-slot:header>
                <tr>
                  <th class="text-left" width="20px">
                    <FormCheckbox :value="allContactsSelected" @click="selectAllClicked($event)" />
                  </th>
                  <th width="30%">Contact</th>
                  <th width="30%">Email Contact Person</th>
                  <th class="text-center" width="20%">
                    <Tooltip helpStyle label="Total invoiced amount owing minus available credit notes / overpayments">Invoice Current</Tooltip>
                  </th>
                  <th class="text-center" width="20%">
                    <Tooltip helpStyle label="Total overdue invoice amount owing">Overdue</Tooltip>
                  </th>
                  <th class="text-center" width="30px">Actions</th>
                </tr>
              </template>
              <template v-slot:body>
                <template v-for="contact in statementContactList">
                  <tr :key="`contact-${contact.id}`">
                    <td width="20px">
                      <FormCheckbox v-model="contact.isSelected" />
                    </td>
                    <td class="font-weight-bold">
                      <LinkContact :id="contact.id" :name="contact.name" />
                    </td>
                    <td>
                      <FormSelect v-model="contact.emailContactPersonId" :options="contact.emailLookup" />
                    </td>
                    <td class="text-center">{{contact.invoiceCurrent | formatCurrencyAccounts}}</td>
                    <td class="text-center">{{contact.invoiceDueTotal | formatCurrencyAccounts}}</td>
                    <td class="text-center">
                      <ActionDropMenu :tableAction="true" :forceDropdown="true">
                        <DropdownMenuItem @click="displayBalanceStatement(contact.id)" icon="fa-file-invoice-dollar">Balance Statement</DropdownMenuItem>
                      </ActionDropMenu>                      
                    </td>
                  </tr>
                </template>

                <tr class="table-totals-row-header font-weight-bold">
                  <td colspan="3">Totals</td>
                  <td class="text-center">{{agedTrialBalanceData.totalInvoiceCurrent | formatCurrencyAccounts}}</td>
                  <td class="text-center">{{agedTrialBalanceData.totalInvoiceDueTotal | formatCurrencyAccounts}}</td>
                  <td class="text-center"></td>
                </tr>
              </template>
              <template v-slot:empty v-if="!statementContactList || statementContactList.length == 0">
                No contact data found for the date range
              </template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>
      
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'
import { mapState } from 'vuex'
import pdfMixin from '@/mixins/pdf'
import form from '@/mixins/form'

import Utils from '@/utils/utils'

import SendContactListStatementDialog from '@/modules/intelligence/views/sendContactListStatementDialog'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateSelect from '@/components/reporting/reportDateSelect'
import ReportContactSelect from '@/components/reporting/reportContactSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import { ContactLabelTypes } from '@/store/types'

export default {
  name: 'AgedTrialBalance',
  mixins: [pdfMixin, form],
  components: {
    ReportFilterPopover,
    ReportDateSelect,
    ReportContactSelect,
    ReportFilterGroup
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      agedTrialBalanceData: {},
      statementContactList: [],

      defaultReceivableFilter: {    
        date: moment().local().endOf('day').toDate().toISOString(),
        contactId: null,
        contactName: ''
      },
      editingReceivableFilter: {},
      appliedReceivableFilter: {},

      minStatementDate: moment().local().startOf('day').toDate().toISOString(),
    }
  },
  computed: {
    allContactsSelected() {
      return this.statementContactList.filter(contact => contact.isSelected).length == this.statementContactList.length
    }
  },
  mounted() {
    this.editingReceivableFilter = _.clone(this.defaultReceivableFilter)
    this.appliedReceivableFilter = _.clone(this.defaultReceivableFilter)

    this.requestSummary()
  },
  beforeDestroy() {
  },
  methods: {
    requestSummary() {
      let date = moment(this.appliedReceivableFilter.date).local().endOf('day').toDate().toISOString()

      let filterData = {
        date: date,
        contactId: this.appliedReceivableFilter.contactId
      }

      this.loading = true
      this.loadingError = ''

      this.$apiService.analyticsService.getAccountsAgedTrialBalance(filterData)
      .then(response => {
        this.agedTrialBalanceData = response.data
        this.statementContactList = _.cloneDeep(response.data.contactList)
        .filter(contact => {
          // By default, don't display anything less than 5c invoiced owing
          if(Math.abs(contact.invoiceTotal) < 0.05)
            return false
          return true
        })
        .map(contact => {
          var emailContactPersonId = null
          var emailLookup = []
          if(_.isArray(contact.contactPeople)) {
            emailLookup = contact.contactPeople.map(contactPerson => {
              return {
                value: contactPerson.id,
                name: `${contactPerson.formattedName} - ${contactPerson.email}`
              }
            })

            // Default to first contact with 'accounts' tag
            // Otherwise, default contact
            // Otherwise, just first contact
            if(contact.contactPeople.length > 0)
              emailContactPersonId = contact.contactPeople[0].id
            let defaultPerson = contact.contactPeople.find(contactPerson => contactPerson.id == contact.defaultPersonId)
            if(defaultPerson)
              emailContactPersonId = defaultPerson.id

            let accountsPerson = contact.contactPeople.find(contactPerson => {
              if(_.isArray(contactPerson.labels)) {
                return (contactPerson.labels.find(label => label.isFixed && label.statusType == ContactLabelTypes.accounts) != null)
              }
              return false
            })
            if(accountsPerson)
              emailContactPersonId = accountsPerson.id
          }

          return _.defaults(contact, {
            emailLookup: emailLookup,
            emailContactPersonId: emailContactPersonId,
            isSelected: emailLookup.length > 0
          })
        })
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },
    displayBalanceStatement(contactId) {
      let date = moment(this.appliedReceivableFilter.date).local().endOf('day').toDate().toISOString()

      let queryString = buildUrl({ queryParams: { date: date } })
      this.displayReportPDF(`/analytics/reports/accounts/${contactId}/balance/report${queryString}`)
    },
    selectAllClicked() {
      let selectAll = this.allContactsSelected

      this.statementContactList.forEach(contact => {
        contact.isSelected = !selectAll
      })      
    },
    sendSelectedStatements() {
      let date = moment(this.appliedReceivableFilter.date).local().endOf('day').toDate().toISOString()

      let selectedContacts = this.statementContactList
      .filter(contact => contact.isSelected && !!contact.emailContactPersonId)
      .map(contact => {
        return {
          id: contact.id,
          name: contact.name,
          contactPersonId: contact.emailContactPersonId
        }
      })

      this.displayModal(SendContactListStatementDialog, { 
        date: date,
        selectedContacts: selectedContacts
      })
      .then((success) => {
        if(success) {
          this.successToast('Statements Sent')
        }
      })
    },
    applyReceivableFilter() {
      this.appliedReceivableFilter = _.clone(this.editingReceivableFilter)

      this.requestSummary()
    },
    resetReceivableFilter() {
      this.editingReceivableFilter = _.clone(this.defaultReceivableFilter)
      this.appliedReceivableFilter = _.clone(this.defaultReceivableFilter)

      this.requestSummary()
    },
  }
}
</script>
